import { cva, type VariantProps } from '@veraclins-dev/cva';

import { styleProps } from './styles';

// Spinner variants — Model A `tone` axis. The spinner is a single animated SVG
// ring, so the cva owns the root box sizing (`spinnerSize`) and the arc stroke
// colour (`tone`). `neutral` (the default) renders the accent/primary stroke;
// the status tones recolour it; `current` inherits `currentColor` so a spinner
// inside a filled Button adopts the button's foreground. Arc colours live in the
// variant map only so every `stroke-*` class stays statically visible to
// Tailwind.
const spinnerVariants = cva({
  base: 'inline-flex flex-none items-center justify-center',
  responsive: {
    ...styleProps,
  },
  variants: {
    spinnerSize: {
      xs: 'size-3',
      sm: 'size-4',
      md: 'size-5',
      lg: 'size-7',
      xl: 'size-10',
    },
    tone: {
      neutral: 'stroke-primary',
      danger: 'stroke-danger',
      success: 'stroke-success',
      warning: 'stroke-warning',
      info: 'stroke-info',
      current: 'stroke-current',
    },
  },
  defaultVariants: {
    spinnerSize: 'md',
    tone: 'neutral',
  },
});

// Faint full-circle track behind the arc, keyed off the same `tone`. Split out
// so `trackVisible={false}` can skip it entirely and `current` can render the
// track at reduced opacity against a coloured surface.
const spinnerTrackVariants = cva({
  variants: {
    tone: {
      neutral: 'stroke-primary-muted',
      danger: 'stroke-danger-muted',
      success: 'stroke-success-muted',
      warning: 'stroke-warning-muted',
      info: 'stroke-info-muted',
      current: 'stroke-current opacity-25',
    },
  },
  defaultVariants: {
    tone: 'neutral',
  },
});

type SpinnerVariants = VariantProps<typeof spinnerVariants>;

export { spinnerTrackVariants, type SpinnerVariants, spinnerVariants };
